import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Template } from '../models';

// Static list of templates
const STATIC_TEMPLATES: Template[] = [
  {
    id: "template-001",
    name: "Classic Black",
    description: "A professional black and white template perfect for all businesses",
    thumbnailUrl: "/img/templates/000000_e6e6e6.jpg",
    previewUrl: "/img/templates/preview/000000_e6e6e6_preview.jpg",
    defaultColor: "#000000",
    defaultColorLight: "#E6E6E6",
    category: 'professional',
    isDefault: true,
    isPremium: false,
    isActive: true
  },
  {
    id: "template-002",
    name: "Ocean Blue",
    description: "A modern blue themed template perfect for tech companies",
    thumbnailUrl: "/img/templates/009FBD_96C9F4.jpg",
    previewUrl: "/img/templates/preview/009FBD_96C9F4_preview.jpg",
    defaultColor: "#009FBD",
    defaultColorLight: "#96C9F4",
    category: 'professional',
    isDefault: false,
    isPremium: false,
    isActive: true
  },
  {
    id: "template-003",
    name: "Bold Red",
    description: "A vibrant red template for creative agencies",
    thumbnailUrl: "/img/templates/FF0000_FFA27F.jpg",
    previewUrl: "/img/templates/preview/FF0000_FFA27F_preview.jpg",
    defaultColor: "#FF0000",
    defaultColorLight: "#FFA27F",
    category: 'professional',
    isDefault: false,
    isPremium: false,
    isActive: true
  },
  {
    id: "template-004",
    name: "Fresh Green",
    description: "An eco-friendly green template for sustainable businesses",
    thumbnailUrl: "/img/templates/399918_ECFFE6.jpg",
    previewUrl: "/img/templates/preview/399918_ECFFE6_preview.jpg",
    defaultColor: "#399918",
    defaultColorLight: "#ECFFE6",
    category: 'professional',
    isDefault: false,
    isPremium: false,
    isActive: true
  },
  {
    id: "template-005",
    name: "Warm Brown",
    description: "A warm and earthy brown template for traditional businesses",
    thumbnailUrl: "/img/templates/543310_AF8F6F.jpg",
    previewUrl: "/img/templates/preview/543310_AF8F6F_preview.jpg",
    defaultColor: "#543310",
    defaultColorLight: "#AF8F6F",
    category: 'professional',
    isDefault: false,
    isPremium: false,
    isActive: true
  },
  {
    id: "template-006",
    name: "Hot Pink",
    description: "A bold and energetic pink template for lifestyle brands",
    thumbnailUrl: "/img/templates/E90074_FF4191.jpg",
    previewUrl: "/img/templates/preview/E90074_FF4191_preview.jpg",
    defaultColor: "#E90074",
    defaultColorLight: "#FF4191",
    category: 'professional',
    isDefault: false,
    isPremium: false,
    isActive: true
  },
  {
    id: "template-007",
    name: "Deep Navy",
    description: "A sophisticated navy blue template for financial services",
    thumbnailUrl: "/img/templates/03346E_6EACDA.jpg",
    previewUrl: "/img/templates/preview/03346E_6EACDA_preview.jpg",
    defaultColor: "#03346E",
    defaultColorLight: "#6EACDA",
    category: 'professional',
    isDefault: false,
    isPremium: false,
    isActive: true
  },
  {
    id: "template-008",
    name: "Sunset Vibes",
    description: "A warm orange and red template for creative and energetic brands",
    thumbnailUrl: "/img/templates/FF8225_B43F3F.jpg",
    previewUrl: "/img/templates/preview/FF8225_B43F3F_preview.jpg",
    defaultColor: "#FF8225",
    defaultColorLight: "#B43F3F",
    category: 'professional',
    isDefault: false,
    isPremium: false,
    isActive: true
  }
];

@Injectable({
  providedIn: 'root'
})
export class TemplateService {

  http = inject(HttpClient);

  private readonly baseApiUrl = `${environment.invoiceServiceApiBaseUrl}/api`;
  private selectedTemplate$ = new BehaviorSubject<Template | null>(null);
  private availableTemplates$ = new BehaviorSubject<Template[]>([]);

  constructor() {
    //this.loadTemplates();
  }

  /**
   * Get all available templates
   */
  getTemplates(): Observable<Template[]> {
    return this.availableTemplates$.asObservable();
  }

  /**
   * Get template by ID
   */
  getTemplate(id: string): Observable<Template> {
    return this.http.get<Template>(`${this.baseApiUrl}/${id}`);
  }

  /**
   * Get currently selected template
   */
  getSelectedTemplate(): Observable<Template | null> {
    return this.selectedTemplate$.asObservable();
  }

  /**
   * Set selected template
   */
  selectTemplate(template: Template): void {
    this.selectedTemplate$.next(template);
  }

  /**
   * Get selected template value synchronously
   */
  getSelectedTemplateValue(): Template | null {
    return this.selectedTemplate$.value;
  }

  /**
   * Search templates by category
   */
  searchByCategory(category: string): Observable<Template[]> {
    return this.http.get<Template[]>(`${this.baseApiUrl}/search?category=${category}`);
  }

  /**
   * Load all templates from API
   */
  private loadTemplates(): void {
    this.http.get<Template[]>(this.baseApiUrl).subscribe(
      (templates: Template[]) => {
        this.availableTemplates$.next(templates);
      },
      (error) => {
        console.error('Failed to load templates:', error);
      }
    );
  }

  /**
   * Refresh templates
   */
  refreshTemplates(): void {
    this.loadTemplates();
  }

  /**
   * Get static templates (local list, no API call)
   */
  getStaticTemplates(): Observable<Template[]> {
    return of(STATIC_TEMPLATES);
  }
}
